import Link from "next/link";

/**
 * WTM Logo — the "M" peaks form castle turrets with rectangular notches.
 * Two variants:
 *   - LogoMark: 32x32 icon (for favicon / small spaces)
 *   - Logo: full mark + "WhatsTheMoat" text (for navbar)
 */

export function LogoMark({
  size = 32,
  color = "white",
  className = "",
}: {
  size?: number;
  color?: "white" | "blue";
  className?: string;
}) {
  const fill = color === "blue" ? "#3B82F6" : "#FFFFFF";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="WTM logo"
    >
      {/* W */}
      <path
        d="M4 18L9 42L15 26L21 42L26 18"
        stroke={fill}
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* T */}
      <path
        d="M28 18H42M35 18V42"
        stroke={fill}
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* M with castle turrets — the two peaks have rectangular notches */}
      <path
        d="M44 42V18L50 10V14H52V10L56 18L60 10V14H62V10L68 18V42"
        stroke={fill}
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

export function Logo({ href = "/" }: { href?: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <Link href={href} className="flex items-center gap-2.5 no-underline" title="WhatsTheMoat">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue to-blue-glow flex items-center justify-center overflow-hidden">
          <svg
            width="28"
            height="28"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="WTM"
          >
            {/* Castle battlement bar across top */}
            <rect x="4" y="4" width="4" height="6" rx="0.5" fill="white" />
            <rect x="11" y="4" width="4" height="6" rx="0.5" fill="white" />
            <rect x="18" y="4" width="4" height="6" rx="0.5" fill="white" />
            <rect x="25" y="4" width="4" height="6" rx="0.5" fill="white" />
            <rect x="32" y="4" width="4" height="6" rx="0.5" fill="white" />
            {/* Connecting wall */}
            <rect x="4" y="9" width="32" height="2.5" fill="white" opacity="0.4" />
            {/* WTM text */}
            <text
              x="20"
              y="28"
              textAnchor="middle"
              fill="white"
              fontSize="14"
              fontWeight="700"
              fontFamily="DM Sans, sans-serif"
              letterSpacing="0.5"
            >
              WTM
            </text>
          </svg>
        </div>
        <span className="font-bold text-lg text-foreground tracking-tight">
          WhatsTheMoat
        </span>
      </Link>
      {/* Phase 3 shell unification: the "BETA · Survey" badge that lived
          here relocated to the SiteFooter Resources column ("Beta survey"). */}
    </div>
  );
}
